/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, computed, effect, forwardRef, input, output, signal, untracked, viewChild } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { NgbDropdown, NgbDropdownModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import { Language } from "../../../constants/enums";
import { MatchWidthDirective } from "../../../directives";
import { ParameterCatalog, ValueText } from "../../../models";
import { FormDropdownTreeNodeComponent } from "./form-dropdown-tree-node";

@Component({
  selector: "adaa-form-dropdown-tree",
  imports: [TranslatePipe, ReactiveFormsModule, NgbDropdownModule, FormDropdownTreeNodeComponent, MatchWidthDirective],
  templateUrl: "./form-dropdown-tree.html",
  styleUrl: "./form-dropdown-tree.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormDropdownTreeComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormDropdownTreeComponent),
    },
  ],
})
export class FormDropdownTreeComponent implements ControlValueAccessor {
  readonly AdaaHelper = AdaaHelper;
  readonly Language = Language;

  readonly ddl = viewChild.required<NgbDropdown>("ddl");

  private readonly _requiredFromValidator = signal<boolean>(false);
  private readonly _imperativeInvalid = signal<boolean>(false);
  private readonly _disabledByFormControl = signal<boolean>(false);
  private _disabledByInput = false;
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: any) => void;

  readonly inputControl = new FormControl();
  selectedValue: any[] = [];
  selectedObjects: ValueText[] = [];
  optionsCopy: ValueText[] = [];
  displayOptions: ValueText[] = [];
  hasSearch: boolean = false;

  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly options = input<ValueText[]>([]);
  readonly isMultiple = input<boolean>(false);
  readonly enableSearch = input<boolean>(true);
  readonly classes = input<string>("");
  readonly setDefaultValue = input<unknown>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());
  readonly isInvalid = computed(() => this.invalid() || this._imperativeInvalid());
  readonly effectivelyDisabled = computed(() => this.isDisabled() || this._disabledByFormControl());

  constructor() {
    effect(() => {
      if (this.isDisabled()) {
        this._disabledByInput = true;
        this.inputControl.disable();
      } else if (this._disabledByInput) {
        this._disabledByInput = false;
        this.inputControl.enable();
      }
    });

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      if (value) {
        untracked(() => this.writeValue(value));
      }
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          this._requiredFromValidator.set(field.mandatory === "Y");
        }
      }
    });

    // Mirrors ngOnChanges: only rebuild when options content actually changes
    effect(() => {
      const opts = this._asOptionsArray(this.options());
      untracked(() => {
        if (JSON.stringify(opts) !== JSON.stringify(this.optionsCopy)) {
          this.displayOptions = opts;
          this.optionsCopy = opts;
          this._setSelectedObjects();
        }
      });
    });
  }

  public writeValue(obj: any): void {
    if (obj === null) {
      this.inputControl.reset();
      return;
    }

    if (obj instanceof Array) this.selectedValue = obj;
    else this.selectedValue = [obj];

    this._setSelectedObjects();
    this.inputControl.setValue(this.selectedValue);
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this._disabledByFormControl.set(isDisabled);
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(control: FormControl): false | { required: boolean } {
    const isEmpty = control.value === null || control.value === undefined || control.value === "";
    const isRequired = this.isRequired() && isEmpty;
    return (
      isRequired && {
        required: true,
      }
    );
  }

  public markInvalid(value: boolean): void {
    this._imperativeInvalid.set(value);
  }

  public valueChanged(event: any[]): void {
    this.selectedValue = event;
    this._setSelectedObjects();

    if (this.isMultiple()) {
      if (this._onChange) this._onChange(event);
      this.inputChanges.emit(event);
    } else {
      // In single mode get the first item only
      const value = event && event.length > 0 ? event[0] : null;
      if (this._onChange) this._onChange(value);
      this.inputChanges.emit(value);

      this.ddl().close();
    }
  }

  public removeSelection(event: any): void {
    this.valueChanged(this.selectedValue.filter((e) => e != event));
  }

  public search(value: string): void {
    if (!AdaaHelper.isDefinedAndNotEmpty(value)) {
      this.displayOptions = AdaaHelper.clone(this.optionsCopy, true);
      this.hasSearch = false;
    } else {
      this.displayOptions = this._searchRecursive(this.optionsCopy, value.toLowerCase());
      this.hasSearch = true;
    }
  }

  private _setSelectedObjects(): void {
    this.selectedObjects = [];
    if (this.selectedValue)
      this.selectedValue.forEach((e) => {
        const item = this._findObjectByIdRecursive(this.displayOptions, e);
        if (item) this.selectedObjects.push(item);
      });
  }

  /**
   * Consumers bind `options` to data that is still loading, so the input can hold
   * `undefined` or any other non-array value. The template iterates `displayOptions`,
   * which throws unless it is always kept an array.
   */
  private _asOptionsArray(options: ValueText[] | null | undefined): ValueText[] {
    return Array.isArray(options) ? options : [];
  }

  private _findObjectByIdRecursive(array: ValueText[], id: any): ValueText | undefined {
    if (!Array.isArray(array)) return undefined;
    for (const obj of array) {
      if (obj.value === id) {
        return obj;
      }
      if (obj.children) {
        const found = this._findObjectByIdRecursive(obj.children, id);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  }

  private _searchRecursive(array: ValueText[], value: string): ValueText[] {
    const matches: ValueText[] = [];
    if (!Array.isArray(array)) return matches;

    array.forEach((e) => {
      if (AdaaHelper.asText(e.text).toLowerCase().includes(value)) {
        matches.push(e);
      } else {
        if (e.children) {
          const result = this._searchRecursive(e.children, value);
          if (result.length) matches.push(Object.assign({}, e, { children: result }));
        }
      }
    });

    return matches;
  }
}
